import { useState, useEffect } from 'react';
import { api } from '../../../api';
import type { BusinessContext, OnboardingStatus } from '../../../types';

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  { value: 'accounting_services',    label: 'Accounting Services' },
  { value: 'auto_repair',            label: 'Auto Repair' },
  { value: 'bakery',                 label: 'Bakery' },
  { value: 'bar_pub',                label: 'Bar / Pub' },
  { value: 'beauty_salon',           label: 'Beauty Salon' },
  { value: 'brewery',                label: 'Brewery' },
  { value: 'car_dealership',         label: 'Car Dealership' },
  { value: 'cafe',                   label: 'Cafe' },
  { value: 'clothing_store',         label: 'Clothing Store' },
  { value: 'consulting',             label: 'Consulting' },
  { value: 'corporation',            label: 'Corporation' },
  { value: 'dental_practice',        label: 'Dental Practice' },
  { value: 'digital_agency',         label: 'Digital Agency' },
  { value: 'ecommerce_platform',     label: 'E-commerce Platform' },
  { value: 'educational_institution',label: 'Educational Institution' },
  { value: 'entertainment',          label: 'Entertainment' },
  { value: 'fast_food',              label: 'Fast Food' },
  { value: 'financial_services',     label: 'Financial Services' },
  { value: 'fitness_center',         label: 'Fitness Centre' },
  { value: 'florist',                label: 'Florist' },
  { value: 'food_establishment',     label: 'Food Establishment' },
  { value: 'government',             label: 'Government' },
  { value: 'gym_fitness',            label: 'Gym & Fitness' },
  { value: 'hair_salon',             label: 'Hair Salon' },
  { value: 'hospital',               label: 'Hospital' },
  { value: 'hotel',                  label: 'Hotel' },
  { value: 'insurance',              label: 'Insurance' },
  { value: 'law_firm',               label: 'Law Firm' },
  { value: 'legal_services',         label: 'Legal Services' },
  { value: 'lodging',                label: 'Lodging' },
  { value: 'marketing_agency',       label: 'Marketing Agency' },
  { value: 'medical_clinic',         label: 'Medical Clinic' },
  { value: 'medical_practice',       label: 'Medical Practice' },
  { value: 'moving_company',         label: 'Moving Company' },
  { value: 'non_profit',             label: 'Non-Profit' },
  { value: 'photography',            label: 'Photography' },
  { value: 'professional_service',   label: 'Professional Service' },
  { value: 'real_estate',            label: 'Real Estate' },
  { value: 'recruitment',            label: 'Recruitment / Staffing' },
  { value: 'restaurant',             label: 'Restaurant' },
  { value: 'retail_store',           label: 'Retail Store' },
  { value: 'school',                 label: 'School' },
  { value: 'software',               label: 'Software / SaaS' },
  { value: 'sports_organization',    label: 'Sports Organisation' },
  { value: 'technology_services',    label: 'Technology Services' },
  { value: 'travel_agency',          label: 'Travel Agency' },
  { value: 'university',             label: 'University' },
  { value: 'web_design',             label: 'Web Design / Development' },
  { value: 'winery',                 label: 'Winery' },
  { value: 'other',                  label: 'Other' },
];

const BUSINESS_MODEL_OPTIONS = [
  { value: 'ecommerce',   label: 'E-commerce' },
  { value: 'saas',        label: 'SaaS' },
  { value: 'service',     label: 'Service' },
  { value: 'publication', label: 'Publication / Media' },
  { value: 'local_only',  label: 'Local Only' },
  { value: 'mixed',       label: 'Mixed' },
];

// ── Validation ────────────────────────────────────────────────────────────────

interface FormErrors {
  coreOffering?: string;
  coreOfferingCategory?: string;
  geographyCountry?: string;
  geographyCity?: string;
}

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.coreOffering.trim()) errors.coreOffering = 'Core offering is required.';
  if (form.coreOffering.trim().length > 500) errors.coreOffering = 'Max 500 characters.';
  if (!form.coreOfferingCategory) errors.coreOfferingCategory = 'Please select a category.';
  if (!form.geographyCountry.trim() || form.geographyCountry.trim().length !== 2)
    errors.geographyCountry = 'Enter a valid 2-letter country code (e.g. GB).';
  if (!form.geographyCity.trim()) errors.geographyCity = 'City is required.';
  if (form.geographyCity.trim().length > 200) errors.geographyCity = 'Max 200 characters.';
  return errors;
}

// ── Form state ────────────────────────────────────────────────────────────────

interface FormState {
  coreOffering: string;
  coreOfferingCategory: string;
  geographyCountry: string;
  geographyCity: string;
  businessModel: string;
  adjacentOfferings: string;
}

function bcToForm(bc: BusinessContext): FormState {
  return {
    coreOffering:         bc.coreOffering,
    coreOfferingCategory: bc.coreOfferingCategory,
    geographyCountry:     bc.geographyCountry.toUpperCase(),
    geographyCity:        bc.geographyCity,
    businessModel:        bc.businessModel ?? '',
    adjacentOfferings:    (bc.adjacentOfferings ?? []).join(', '),
  };
}

const EMPTY_FORM: FormState = {
  coreOffering:         '',
  coreOfferingCategory: '',
  geographyCountry:     '',
  geographyCity:        '',
  businessModel:        '',
  adjacentOfferings:    '',
};

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  domainId: string;
  domain: string;
  status: OnboardingStatus;
  onStatusUpdate: (next: OnboardingStatus) => void;
  onContinue: () => void;
  onSkip: () => void;
}

export function BusinessProfileStep({ domainId, domain, status, onStatusUpdate, onContinue, onSkip }: Props) {
  const [bc, setBc] = useState<BusinessContext | null | 'loading'>('loading');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [clearingConfirm, setClearingConfirm] = useState(false);

  // Fetch current business context
  useEffect(() => {
    api.domains
      .getBusinessContext(domainId)
      .then((data) => {
        setBc(data);
        if (data) setForm(bcToForm(data));
      })
      .catch(() => setBc(null));
  }, [domainId]);

  function handleField(field: keyof FormState, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((e) => ({ ...e, [field]: undefined }));
    }
  }

  async function handleSave() {
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const adjacentOfferings = form.adjacentOfferings
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const updated = await api.domains.patchBusinessContext(domainId, {
        coreOffering:         form.coreOffering.trim(),
        coreOfferingCategory: form.coreOfferingCategory,
        adjacentOfferings:    adjacentOfferings.length > 0 ? adjacentOfferings : null,
        geographyCountry:     form.geographyCountry.trim().toUpperCase(),
        geographyCity:        form.geographyCity.trim(),
        businessModel:        form.businessModel || null,
      });
      setBc(updated);
      // Refresh onboarding status
      const nextStatus = await api.onboarding.getStatus(domainId);
      onStatusUpdate(nextStatus);
      onContinue();
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleClearConfirmation() {
    setClearingConfirm(true);
    try {
      await api.domains.clearBusinessContextConfirm(domainId);
      const refreshed = await api.domains.getBusinessContext(domainId);
      setBc(refreshed);
      if (refreshed) setForm(bcToForm(refreshed));
    } catch {
      // non-fatal
    } finally {
      setClearingConfirm(false);
    }
  }

  if (bc === 'loading') {
    return (
      <div className="animate-pulse space-y-4" data-testid="bp-loading">
        <div className="h-6 bg-gray-100 rounded w-1/3" />
        <div className="h-4 bg-gray-100 rounded w-2/3" />
        <div className="h-32 bg-gray-100 rounded" />
      </div>
    );
  }

  const isConfirmed = bc !== null && bc.customerConfirmedAt !== null;

  return (
    <div className="space-y-6" data-testid="business-profile-step">
      <div>
        <h2 className="text-xl font-semibold text-brand-ink">Business Profile</h2>
        <p className="text-sm text-brand-muted mt-1">
          {bc && bc.confidenceScore > 0
            ? 'We extracted the following from your site. Review and correct any details.'
            : 'Tell us about your business so we can generate accurate keyword queries.'}
        </p>
      </div>

      {/* Unverified banner */}
      {!status.verified && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
          We can extract this automatically after your snippet is installed. You can still fill it in
          manually now.
        </div>
      )}

      {/* Customer-confirmed badge */}
      {isConfirmed && bc && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <p className="text-sm text-green-700">
            Confirmed by you on{' '}
            <span className="font-medium">
              {new Date(bc.customerConfirmedAt!).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
            . Auto-extraction is paused for this domain.
          </p>
          <button
            type="button"
            data-testid="re-extract-btn"
            disabled={clearingConfirm}
            onClick={() => void handleClearConfirmation()}
            className="ml-4 text-xs text-green-700 underline hover:no-underline disabled:opacity-50"
          >
            {clearingConfirm ? 'Clearing…' : 'Re-extract from site'}
          </button>
        </div>
      )}

      {/* Form */}
      <div className="space-y-4">
        {/* Core offering */}
        <div>
          <label htmlFor="bp-core-offering" className="block text-sm font-medium text-brand-ink mb-1">
            Core offering <span className="text-red-500">*</span>
          </label>
          <input
            id="bp-core-offering"
            type="text"
            data-testid="input-core-offering"
            value={form.coreOffering}
            onChange={(e) => handleField('coreOffering', e.target.value)}
            placeholder="e.g. Emergency plumbing repairs"
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green ${
              errors.coreOffering ? 'border-red-400' : 'border-gray-200'
            }`}
          />
          {errors.coreOffering && (
            <p className="mt-1 text-xs text-red-500" data-testid="error-core-offering">{errors.coreOffering}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label htmlFor="bp-category" className="block text-sm font-medium text-brand-ink mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            id="bp-category"
            data-testid="input-category"
            value={form.coreOfferingCategory}
            onChange={(e) => handleField('coreOfferingCategory', e.target.value)}
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green bg-white ${
              errors.coreOfferingCategory ? 'border-red-400' : 'border-gray-200'
            }`}
          >
            <option value="">Select a category…</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {errors.coreOfferingCategory && (
            <p className="mt-1 text-xs text-red-500" data-testid="error-category">{errors.coreOfferingCategory}</p>
          )}
        </div>

        {/* Geography row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="bp-country" className="block text-sm font-medium text-brand-ink mb-1">
              Country code <span className="text-red-500">*</span>
            </label>
            <input
              id="bp-country"
              type="text"
              data-testid="input-country"
              value={form.geographyCountry}
              onChange={(e) => handleField('geographyCountry', e.target.value.toUpperCase().slice(0, 2))}
              placeholder="GB"
              maxLength={2}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green ${
                errors.geographyCountry ? 'border-red-400' : 'border-gray-200'
              }`}
            />
            {errors.geographyCountry && (
              <p className="mt-1 text-xs text-red-500" data-testid="error-country">{errors.geographyCountry}</p>
            )}
          </div>
          <div>
            <label htmlFor="bp-city" className="block text-sm font-medium text-brand-ink mb-1">
              City <span className="text-red-500">*</span>
            </label>
            <input
              id="bp-city"
              type="text"
              data-testid="input-city"
              value={form.geographyCity}
              onChange={(e) => handleField('geographyCity', e.target.value)}
              placeholder="London"
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green ${
                errors.geographyCity ? 'border-red-400' : 'border-gray-200'
              }`}
            />
            {errors.geographyCity && (
              <p className="mt-1 text-xs text-red-500" data-testid="error-city">{errors.geographyCity}</p>
            )}
          </div>
        </div>

        {/* Business model */}
        <div>
          <label htmlFor="bp-model" className="block text-sm font-medium text-brand-ink mb-1">
            Business model <span className="text-xs text-brand-muted font-normal">(optional)</span>
          </label>
          <select
            id="bp-model"
            data-testid="input-business-model"
            value={form.businessModel}
            onChange={(e) => handleField('businessModel', e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green bg-white"
          >
            <option value="">Not specified</option>
            {BUSINESS_MODEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Adjacent offerings */}
        <div>
          <label htmlFor="bp-adjacent" className="block text-sm font-medium text-brand-ink mb-1">
            Adjacent offerings <span className="text-xs text-brand-muted font-normal">(comma-separated, optional)</span>
          </label>
          <input
            id="bp-adjacent"
            type="text"
            data-testid="input-adjacent"
            value={form.adjacentOfferings}
            onChange={(e) => handleField('adjacentOfferings', e.target.value)}
            placeholder="e.g. Boiler servicing, Drain unblocking"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green"
          />
        </div>
      </div>

      {saveError && (
        <p className="text-sm text-red-500" data-testid="save-error">{saveError}</p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-4 pt-2">
        <button
          type="button"
          data-testid="save-btn"
          disabled={saving}
          onClick={() => void handleSave()}
          className="px-5 py-2.5 bg-brand-green text-white rounded-lg text-sm font-medium hover:bg-brand-green/90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save & continue'}
        </button>
        <button
          type="button"
          data-testid="skip-btn"
          onClick={onSkip}
          className="text-sm text-brand-muted hover:text-brand-ink"
        >
          I'll come back to this
        </button>
      </div>

      {bc && (
        <p className="text-xs text-gray-300">
          {domain} · v{bc.version} · {bc.confidenceScore > 0 ? 'auto-extracted' : 'awaiting extraction'} · confidence {bc.confidenceScore}/100
        </p>
      )}
    </div>
  );
}
